<script setup lang="ts" generic="T extends string">
/**
 * Choix unique en grille de tuiles (icône, libellé, montant facultatif), sans écran
 * supplémentaire : le geste le plus court pour choisir une enveloppe (spec §5.1).
 * La tuile choisie porte un filet bleu ; aucune ombre.
 */
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui'
import { useId } from 'vue'

import type { IconName } from '../icons/registry'
import Amount from './Amount.vue'
import Icon from './Icon.vue'

export interface GridOption<V extends string> {
  value: V
  label: string
  icon?: IconName
  amount?: number
  /** Repère discret (ex. « Dernière ») : une indication, jamais une sélection. */
  tag?: string
}

defineProps<{ label: string; options: readonly GridOption<T>[]; error?: string | undefined }>()
const model = defineModel<T | undefined>()
const id = useId()
</script>

<template>
  <div class="flex flex-col gap-2">
    <p :id="`${id}-label`" class="text-label text-ink">{{ label }}</p>
    <RadioGroupRoot
      v-model="model"
      :aria-labelledby="`${id}-label`"
      :aria-describedby="error ? `${id}-error` : undefined"
      class="grid grid-cols-2 gap-2 sm:grid-cols-3"
    >
      <RadioGroupItem
        v-for="option in options"
        :key="option.value"
        :value="option.value"
        class="flex min-h-control min-w-0 items-center gap-2.5 rounded-control border border-line px-3 py-2 text-left transition-colors duration-80 active:bg-surface data-[state=checked]:border-accent-strong data-[state=checked]:bg-accent-soft"
      >
        <Icon v-if="option.icon" :name="option.icon" :size="20" class="shrink-0 text-ink-muted" />
        <span class="flex min-w-0 flex-col">
          <span class="truncate text-label text-ink">{{ option.label }}</span>
          <span class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <Amount
              v-if="option.amount !== undefined"
              :value="option.amount"
              size="label"
              tone="muted"
            />
            <span
              v-if="option.tag"
              class="max-w-full truncate rounded-full bg-surface px-1.5 text-caption text-ink-muted"
              >{{ option.tag }}</span
            >
          </span>
        </span>
      </RadioGroupItem>
    </RadioGroupRoot>
    <p
      v-if="error"
      :id="`${id}-error`"
      class="flex items-start gap-1.5 text-caption text-danger"
      role="alert"
    >
      <Icon name="alert" :size="16" />
      <span>{{ error }}</span>
    </p>
  </div>
</template>
